import React from 'react'
import { useMediaQuery } from 'react-responsive'
import { CustomImage, RemoteImage } from '~/models'
import { memo } from '~/ui/component'
import { colors, createUseStyles, layout, ThemeProvider, useStyling } from '~/ui/styling'
import { ImageView } from '../app/media'
import { screenMinWidths } from '../styling/layout'
import { isReactText } from '../util'
import { Label } from './label'
import { HBox, VBox, VBoxProps } from './layout'
import { linearGradient } from '../styling/colors'

export interface Props extends Omit<VBoxProps, 'title'> {
  title?:            React.ReactNode
  image?:            CustomImage
  backgroundImage?:  RemoteImage
  darkenBackground?: boolean
  fadeBackground?:   boolean
}

const Hero = memo('Hero', (props: Props) => {

  const {
    title,
    image,
    children,
    backgroundImage,
    darkenBackground = backgroundImage != null,
    fadeBackground = false,
    ...rest
  } = props

  const {colors} = useStyling()

  const isMobile = !useMediaQuery({minWidth: screenMinWidths.tablet})

  //------
  // Rendering

  const $ = useStyles()

  const inlineStyle = backgroundImage == null ? {} : {
    backgroundImage: `url(${backgroundImage.url})`,
  }

  const dark = backgroundImage != null ? true : colors.semantic.primary.isDark()

  function render() {
    return (
      <ThemeProvider dark={dark}>
        <VBox align={image == null ? 'left' : 'center'} classNames={$.Hero} style={inlineStyle} {...rest}>
          {darkenBackground && <div classNames={$.backgroundImageOverlay}/>}
          {renderContent()}
          {fadeBackground && <div classNames={$.backgroundImageFade}/>}
        </VBox>
      </ThemeProvider>
    )
  }

  function renderContent() {
    if (isMobile) {
      return (
        <VBox classNames={$.content}>
          {renderBody()}
        </VBox>
      )
    }

    return (
      <HBox flex='grow' classNames={$.content} gap={layout.padding.xl}>
        <VBox flex>
          {renderBody()}
        </VBox>
        {image != null && (
          <VBox flex>
            {renderImage()}
          </VBox>
        )}
      </HBox>
    )
  }

  function renderBody() {
    return (
      <VBox flex='grow' gap={layout.padding.l}>
        {renderTitle()}
        {isMobile && renderImage()}
        {children}
      </VBox>
    )
  }

  function renderTitle() {
    if (title == null) { return null }

    return isReactText(title) ? (
      <Label h1 truncate={false}>
        {title}
      </Label>
    ) : title
  }

  function renderImage() {
    if (image == null) { return null }

    return (
      <ImageView
        classNames={$.image}
        source={image}
      />
    )
  }

  return render()

})

export default Hero

export const imageRadius  = 6
export const contentWidth = 1024

const useStyles = createUseStyles(theme => ({
  Hero: {
    position:           'relative',
    backgroundColor:    theme.colors.semantic.primary,
    backgroundSize:     'cover',
    backgroundPosition: 'center center',
    overflow:           'hidden',
  },

  content: {
    position: 'relative',
    zIndex: 20,
    width: '100%',
    maxWidth: contentWidth,
    padding: [layout.padding.xxl.mobile, layout.padding.m.mobile],
    ...layout.tablet({
      padding: [layout.padding.xxl.tablet, layout.padding.xl.tablet],
      maxWidth: '80%',
    }),
    ...layout.widescreen({
      padding: [layout.padding.xxl.desktop, layout.padding.xl.desktop],
    }),
  },

  backgroundImageOverlay: {
    backgroundColor: colors.shim.dark,
    ...layout.overlay,
  },

  backgroundImageFade: {
    background: linearGradient('top', theme.colors.bg.normal.alpha(0), theme.colors.bg.normal),
    height: 64,
    marginTop: -32,
    width: '100%',
    position: 'relative',
  },

  image: {
    borderRadius: 6,
  },
}))